import RefreshButton from "../../../components/ui/RefreshButton";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "../../../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../../../lib/api";
import { Button } from "../../../components/ui";
import {
  OperatorError,
  OperatorLoading,
  operatorSelectClass,
} from "../../admin/operator/components/OperatorFeedback";

type Row = {
  _id?: string;
  id?: string;
  title?: string;
  name?: string;
  sourceName?: string;
  image?: string;
  thumbnailUrl?: string;
  thumbnail?: string;
  thumbUrl?: string;
  status?: string;
  updatedAt?: string;
  createdAt?: string;
  progress?: { completed?: number; total?: number };
};
type Result = { rows: Row[]; next: string | null };

// Reuse tenant-scoped, bounded read APIs. Merchant editors and upload controls
// never mount in support mode; its query cache is discarded on every exit.
export default function SupportLibraryPage({ path }: { path: string }) {
  const { t, locale } = useTranslation();
  const { supportSession } = useAuth();
  const [params, setParams] = useSearchParams();
  const jobs = path === "/downloads";
  const kind = params.get("kind") === "template" ? "template" : "mockup";
  const cursor = params.get("cursor") || "";
  const search = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  const key = jobs ? "jobs" : path.slice(1);
  const number = new Intl.NumberFormat(locale);
  const query = useQuery<Result>({
    queryKey: ["support-library", supportSession?.id, path, kind, cursor, search],
    gcTime: 0,
    queryFn: async () => {
      const endpoint = jobs
        ? `/api/${kind === "template" ? "template-batch" : "mockup-generation"}/jobs`
        : path === "/designs"
          ? "/api/design-assets"
          : `/api${path}`;
      const { data } = await api.get(endpoint, {
        params: {
          limit: 30,
          ...(jobs
            ? { page: Number(cursor) || 1 }
            : path === "/designs"
              ? { after: cursor || undefined, search }
              : { cursor: cursor || undefined, q: search, view: "summary" }),
        },
      });
      return jobs
        ? {
            rows: data.data,
            next: data.pagination.hasMore ? String(data.pagination.page + 1) : null,
          }
        : { rows: path === "/designs" ? data.assets : data.items, next: data.nextCursor };
    },
  });
  const update = (values: Record<string, string>) => setParams(values);
  return (
    <main id="support-main" className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-balance">
            {t(`operatorAdmin.support.destinations.${key}`)}
          </h1>
          <p className="mt-2 max-w-prose text-sm text-on-surface-variant">
            {t("operatorAdmin.support.libraryHint")}
          </p>
        </div>
        <RefreshButton variant="secondary" icon="refresh" onClick={() => query.refetch()}>
          {t("operatorAdmin.overview.refresh")}
        </RefreshButton>
      </div>
      {jobs ? (
        <label className="mb-5 block max-w-xs text-sm">
          {t("operatorAdmin.renderJobs.kind")}
          <select
            className={`${operatorSelectClass} mt-2`}
            value={kind}
            name="kind"
            onChange={(e) => update({ kind: e.target.value })}
          >
            <option value="mockup">{t("operatorAdmin.renderJobs.mockup")}</option>
            <option value="template">{t("operatorAdmin.renderJobs.template")}</option>
          </select>
        </label>
      ) : (
        <form
          className="mb-5 flex max-w-lg gap-2"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            update({ q: String(new FormData(e.currentTarget).get("q") || "").trim() });
          }}
        >
          <label className="min-w-0 flex-1">
            <span className="sr-only">{t("operatorAdmin.support.search")}</span>
            <input
              type="search"
              name="q"
              autoComplete="off"
              maxLength={200}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              className={operatorSelectClass}
              placeholder={t("operatorAdmin.support.searchPlaceholder")}
            />
          </label>
          <Button type="submit" variant="secondary" icon="search">
            {t("operatorAdmin.support.search")}
          </Button>
        </form>
      )}
      {query.isError && (
        <OperatorError stale={Boolean(query.data)} onRetry={() => query.refetch()} />
      )}
      {query.isPending && <OperatorLoading />}
      {query.data && (
        <>
          {query.data.rows.length ? (
            <ul className="divide-y divide-outline-variant border-y border-outline-variant">
              {query.data.rows.map((row) => {
                const id = row._id || row.id || "";
                const preview = row.thumbnailUrl || row.thumbUrl || row.thumbnail || row.image;
                const date = row.updatedAt || row.createdAt;
                return (
                  <li key={id} className="flex min-w-0 items-center gap-4 py-4">
                    {!jobs &&
                      (preview ? (
                        <img
                          src={preview}
                          alt=""
                          width={72}
                          height={72}
                          loading="lazy"
                          className="h-[72px] w-[72px] shrink-0 rounded-lg bg-surface-container object-contain"
                        />
                      ) : (
                        <span
                          className="material-symbols-outlined flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-lg bg-surface-container text-on-surface-variant"
                          aria-hidden="true"
                        >
                          image
                        </span>
                      ))}
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-sm font-medium">
                        {row.title ||
                          row.name ||
                          row.sourceName ||
                          t("operatorAdmin.support.untitled")}
                      </p>
                      <p className="mt-1 break-all font-mono text-xs text-on-surface-variant">
                        {id}
                      </p>
                      {date && (
                        <time
                          dateTime={date}
                          className="mt-1 block text-xs text-on-surface-variant"
                        >
                          {new Date(date).toLocaleString(locale === "de" ? "de-DE" : "en-GB")}
                        </time>
                      )}
                    </div>
                    {jobs && (
                      <div className="shrink-0 text-right text-xs">
                        <span>{t(`operatorAdmin.renderJobs.states.${row.status}`)}</span>
                        <p className="mt-2 tabular-nums text-on-surface-variant">
                          {number.format(row.progress?.completed || 0)} /{" "}
                          {number.format(row.progress?.total || 0)}
                        </p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p
              role="status"
              className="border-y border-outline-variant py-12 text-center text-sm text-on-surface-variant"
            >
              {t("operatorAdmin.support.empty")}
            </p>
          )}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-on-surface-variant">
              {t("operatorAdmin.support.pageCount", {
                count: number.format(query.data.rows.length),
              })}
            </p>
            <div className="flex gap-2">
              {cursor && (
                <Button variant="secondary" onClick={() => update({ q: search, kind })}>
                  {t("operatorAdmin.support.firstPage")}
                </Button>
              )}
              {query.data.next && (
                <Button
                  variant="secondary"
                  onClick={() => update({ q: search, kind, cursor: query.data.next! })}
                >
                  {t("operatorAdmin.support.nextPage")}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </main>
  );
}
